import React, { useState, useEffect } from 'react';
import { Check, CircleCheck, Rocket, TrendingUp, Zap, Building2, Users, Eye, Gift } from 'lucide-react';
import subscriptionApi, { SubscriptionTier } from '../../services/subscriptionApi';

interface TierSelectorCardsProps {
  selectedTierId?: number | null;
  onTierSelect: (tier: SubscriptionTier) => void;
  filterCodes?: string[];
  disabled?: boolean;
  showTrialBadge?: boolean;
  unlockedTier?: SubscriptionTier | null;
softLaunchMode?: boolean;
  softLaunchDays?: number;
  parityDiscountPct?: number | null;
  parityCountry?: string | null;
  api?: {
    getTiers: (filterCodes?: string[]) => Promise<SubscriptionTier[]>;
  };
}

// Helper to get tier icon based on tier_code or icon_name
const getTierIcon = (tier: SubscriptionTier): React.ElementType => {
  const code = tier.icon_name || tier.tier_code;
  switch (code) {
    case 'starter':
    case 'rocket':
      return Rocket;
    case 'growth':
    case 'professional':
    case 'trending_up':
      return TrendingUp;
    case 'scale':
    case 'bolt':
      return Zap;
    case 'enterprise':
    case 'business':
      return Building2;
    default:
      return Rocket;
  }
};

// Helper to get gradient based on tier_code or icon_name
const getTierGradient = (tier: SubscriptionTier): string => {
  const code = tier.icon_name || tier.tier_code;
  switch (code) {
    case 'starter':
    case 'rocket':
      return 'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)';
    case 'growth':
    case 'professional':
    case 'trending_up':
      return 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)';
    case 'scale':
    case 'bolt':
      return 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)';
    case 'enterprise':
    case 'business':
      return 'linear-gradient(135deg, #0f172a 0%, #334155 100%)';
    default:
      return 'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)';
  }
};

const isPopularTier = (tierCode: string) => {
  return tierCode === 'growth' || tierCode === 'professional';
};

const TierSelectorCards: React.FC<TierSelectorCardsProps> = ({
  selectedTierId,
  onTierSelect,
  filterCodes = ['starter', 'professional'],
  disabled = false,
  showTrialBadge = true,
  unlockedTier = null,
softLaunchMode = false,
  softLaunchDays = 90,
  parityDiscountPct = null,
  parityCountry = null,
  api,
}) => {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredTier, setHoveredTier] = useState<number | null>(null);

  useEffect(() => {
    loadTiers();
  }, [filterCodes.join(',')]);

  const loadTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedTiers = api ? await api.getTiers(filterCodes) : await subscriptionApi.getTiers(filterCodes);
      setTiers(fetchedTiers);

      if (unlockedTier && selectedTierId === unlockedTier.id) {
        return;
      }

      if (!selectedTierId && fetchedTiers.length > 0) {
        if (unlockedTier) {
          onTierSelect(unlockedTier);
        } else {
          const popularTier = fetchedTiers.find((t) => isPopularTier(t.tier_code));
          const defaultTier = popularTier || fetchedTiers[0];
          onTierSelect(defaultTier);
        }
      }
    } catch (err: any) {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const displayTiers = React.useMemo(() => {
    let result = tiers;

    if (unlockedTier) {
      const alreadyInList = tiers.some((t) => t.id === unlockedTier.id);
      if (!alreadyInList) {
        result = [unlockedTier, ...tiers];
      }
      result = result.filter((t) => t.tier_code !== 'enterprise');
    }

    return result;
  }, [tiers, unlockedTier]);

const parityFactor = parityDiscountPct && parityDiscountPct > 0 ? (100 - parityDiscountPct) / 100 : 1;
  const flagEmoji = (cc: string | null): string =>
    cc && cc.length === 2
      ? cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
      : '';

  const formatPrice = (tier: SubscriptionTier): string => {
    if (tier.is_contact_sales) return 'Custom';
    return `$${Math.round(parseFloat(tier.price_monthly) * parityFactor)}`;
  };
  const formatOriginalPrice = (tier: SubscriptionTier): string =>
    tier.is_contact_sales ? '' : `$${Math.round(parseFloat(tier.price_monthly))}`;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px' }}>
        <div
          style={{
            width: '28px',
            height: '28px',
            border: '2px solid var(--v2-border)',
            borderTopColor: 'var(--v2-primary)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: 'var(--v2-error-bg)',
          border: '1px solid #fecaca',
          borderRadius: 'var(--radius-md)',
          padding: '16px',
          color: '#991b1b',
          fontSize: '0.875rem',
        }}
      >
        {error}
      </div>
    );
  }

return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: softLaunchMode 
          ? `repeat(${Math.min(displayTiers.length + 1, 4)}, minmax(220px, 1fr))`
          : `repeat(${Math.min(displayTiers.length, 4)}, minmax(220px, 1fr))`,
        gap: '16px',
      }}
    >
      {/* Early Access Card - Only in soft launch mode */}
      {softLaunchMode && (
        <div
          style={{
            position: 'relative',
            backgroundColor: 'var(--v2-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '2px solid var(--v2-primary)',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)',
            transform: 'translateY(-2px)',
            overflow: 'visible',
          }}
        >
          {/* Early Access Badge */}
          <div style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)',
            color: 'white',
            fontSize: '0.625rem',
            fontWeight: 700,
            padding: '4px 12px',
            borderRadius: 'var(--radius-full)',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(217, 119, 6, 0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Early Access
          </div>
          <div style={{ padding: '20px', paddingTop: '28px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Gift size={18} strokeWidth={2} color="#ffffff" />
              </div>
              <div style={{ flex: 1, fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                Early Access
              </div>
              <CircleCheck size={22} strokeWidth={2} color="var(--v2-primary)" />
            </div>
            {/* Price */}
            <div style={{ marginBottom: '16px' }}>
              <span style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--v2-text)', letterSpacing: '-0.02em' }}>
                $0
              </span>
              <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', marginLeft: '4px' }}>
                for {softLaunchDays} days
              </span>
            </div>
            {/* Features */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'var(--v2-surface-alt)',
              borderRadius: 'var(--radius-md)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Features</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>Full Access</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Support</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>Priority</span>
              </div>
            </div>
            {/* Selected Button */}
<button type="button" disabled style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '10px 16px',
              backgroundColor: 'var(--v2-primary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'default',
            }}>
              <Check size={16} strokeWidth={2.5} />
              Selected
            </button>
          </div>
        </div>
      )}
      {displayTiers.map((tier) => {
        const isSelected = selectedTierId === tier.id;
        const isPopular = isPopularTier(tier.tier_code);
        const isUnlocked = unlockedTier?.id === tier.id;
        const isHovered = hoveredTier === tier.id;
        const TierIcon = getTierIcon(tier);
        const tierGradient = getTierGradient(tier);

const isSoftLaunchDisabled = softLaunchMode;
        return (
          <div
            key={tier.id}
            onClick={() => !disabled && !isSoftLaunchDisabled && !tier.is_contact_sales && onTierSelect(tier)}
            onMouseEnter={() => !isSoftLaunchDisabled && setHoveredTier(tier.id)}
            onMouseLeave={() => setHoveredTier(null)}
            style={{
              position: 'relative',
              backgroundColor: 'var(--v2-surface)',
              borderRadius: 'var(--radius-lg)',
              border: `2px solid ${isSelected && !isSoftLaunchDisabled ? 'var(--v2-primary)' : isHovered ? 'var(--v2-text-muted)' : 'var(--v2-border)'}`,
              boxShadow: isSelected && !isSoftLaunchDisabled ? '0 4px 12px rgba(79, 70, 229, 0.15)' : 'var(--shadow-sm)',
              cursor: tier.is_contact_sales || disabled || isSoftLaunchDisabled ? 'default' : 'pointer',
              transition: 'all 0.2s ease',
              transform: isSelected && !isSoftLaunchDisabled ? 'translateY(-2px)' : 'none',
              overflow: 'visible',
              opacity: isSoftLaunchDisabled ? 0.5 : 1,
              filter: isSoftLaunchDisabled ? 'grayscale(30%)' : 'none',
              pointerEvents: isSoftLaunchDisabled ? 'none' : 'auto',
            }}
          >
            {/* Coming Soon Badge (soft launch) */}
            {isSoftLaunchDisabled && (
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                backgroundColor: 'var(--v2-surface)',
                border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px 8px',
                fontSize: '0.6875rem',
                color: 'var(--v2-text-muted)',
                fontWeight: 500,
                zIndex: 1,
              }}>
                Coming Soon
              </div>
            )}
            {/* Unlocked Badge */}
            {isUnlocked && (
              <div
                style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                  color: 'white',
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  padding: '4px 12px',
                  borderRadius: 'var(--radius-full)',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Check size={10} strokeWidth={3} />
                Unlocked
              </div>
            )}

            {/* Popular Badge */}
            {isPopular && !isUnlocked && (
              <div
                style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: 'white',
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  padding: '4px 12px',
                  borderRadius: 'var(--radius-full)',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Recommended
              </div>
            )}

            <div style={{ padding: '20px', paddingTop: isPopular || isUnlocked ? '28px' : '20px' }}>
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: 'var(--radius-md)',
                    background: tierGradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TierIcon size={18} strokeWidth={2} color="#ffffff" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--v2-text)',
                    }}
                  >
                    {tier.tier_name}
                  </div>
                </div>
                {isSelected && <CircleCheck size={22} strokeWidth={2} color="var(--v2-primary)" />}
              </div>

{/* Price */}
              <div style={{ marginBottom: '16px' }}>
                {parityFactor < 1 && !tier.is_contact_sales && (
                  <span
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'var(--v2-text-tertiary)',
                      textDecoration: 'line-through',
                      marginRight: '8px',
                      verticalAlign: 'middle',
                    }}
                  >
                    {formatOriginalPrice(tier)}
                  </span>
                )}
                <span
                  style={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: 'var(--v2-text)',
                    letterSpacing: '-0.02em',
                    verticalAlign: 'middle',
                  }}
                >
                  {formatPrice(tier)}
                </span>
                {!tier.is_contact_sales && (
                  <span
                    style={{
                      fontSize: '0.875rem',
                      color: 'var(--v2-text-tertiary)',
                      marginLeft: '4px',
                    }}
                  >
                    /mo
                  </span>
                )}
                {parityFactor < 1 && !tier.is_contact_sales && (
                  <div style={{ marginTop: '6px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        backgroundColor: '#0e7490',
                        color: '#ffffff',
                        padding: '3px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                      }}
                    >
                      {flagEmoji(parityCountry)} {parityCountry || 'Regional'} pricing · −{parityDiscountPct}%
                    </span>
                  </div>
                )}
                {showTrialBadge && tier.trial_duration_days > 0 && (
                  <div style={{ marginTop: '6px' }}>
                    <span
                      style={{
                        backgroundColor: 'var(--v2-success-bg)',
                        color: 'var(--v2-success-dark)',
                        padding: '3px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                      }}
                    >
                      {tier.trial_duration_days}-day free trial
                    </span>
                  </div>
                )}
              </div>

              {/* Key Metrics */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'var(--v2-surface-alt)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Users size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
                    <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Monthly Active Users (MAU)</span>
                  </div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                    {tier.base_named_users ?? 'Custom'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Eye size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
                    <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Public View</span>
                  </div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                    {tier.base_public_view_users ?? 'Custom'}
                  </span>
                </div>
              </div>

              {/* Additional Limits */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--v2-success-bg)',
                      color: 'var(--v2-success-dark)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Check size={10} strokeWidth={3} />
                  </span>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                    <strong style={{ fontWeight: 600, color: 'var(--v2-text-secondary)' }}>
                      {tier.base_api_endpoints ?? 'Custom'}
                    </strong>{' '}
                    API Endpoints
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--v2-success-bg)',
                      color: 'var(--v2-success-dark)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Check size={10} strokeWidth={3} />
                  </span>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                    <strong style={{ fontWeight: 600, color: 'var(--v2-text-secondary)' }}>
                      {tier.base_scheduled_jobs ?? 'Custom'}
                    </strong>{' '}
                    Scheduled Jobs
                  </span>
                </div>
              </div>

              {/* Action Button */}
              {tier.is_contact_sales ? (
                <a
                  href="mailto:sales@dashboardfox.com"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '10px 16px',
                    backgroundColor: 'var(--v2-surface-alt)',
                    color: 'var(--v2-text)',
                    border: '1px solid var(--v2-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'all 0.15s',
                    opacity: disabled ? 0.5 : 1,
                    pointerEvents: disabled ? 'none' : 'auto',
                  }}
                >
                  Contact Sales
                </a>
              ) : (
                <button
                  disabled={disabled}
                  type="button"   
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px 16px',
                    backgroundColor: isSelected ? 'var(--v2-primary)' : 'var(--v2-surface)',
                    color: isSelected ? '#ffffff' : 'var(--v2-text)',
                    border: isSelected ? 'none' : '1px solid var(--v2-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  {isSelected && <Check size={16} strokeWidth={2.5} />}
                  {isSelected ? 'Selected' : 'Select'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TierSelectorCards;
